/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow
import type { Node } from 'react';

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Button, Col, Row } from 'reactstrap';
import Header from '#components/support/layout/Header/index.js';
import Main from '#components/support/layout/MainPageContainer/index.jsx';
import Page from '#components/support/layout/Page/index.js';
import { MODELS, UPLOAD } from '#constants/pathStrings.js';
import resolveExtractionJobAction from '#support/models/extraction/resolveExtractionJobAction.js';
import TryAnotherDocumentButton from '../support/TryAnotherDocumentButton/TryAnotherDocumentButton.jsx';
import * as layout from '../support/reportReaderLayout.module.scss';
import * as styles from './ExtractionProgress.module.scss';
import ExtractionStages from './ExtractionStages/ExtractionStages.jsx';

type Props = {|
  model: ?{ modelName: ?string },
  routeParams: {| modelId: string |},
  location: {| state: ?{ jobId: ?string } |},
  navigate: Function,
  fetchModel: Function,
  getExtractionJob: Function,
  rehydrateExtractionJob: Function,
  chooseUploadReport: Function,
|};

const POLL_INTERVAL_MS = 3000;
// Two writers end a job with a status only and no `reason`: the dead-letter consumer (after the
// queue's retries) and the stale sweep (a run that stopped responding). The intro already says
// SP3-AI couldn't finish, so this only has to say why there is nothing more to show, and must not
// claim retries for a job that was swept.
const NO_REASON_FALLBACK = {
  EXTRACTION_ERROR_DLQ: 'Reading stopped after several attempts.',
  DEFAULT: 'Reading stopped before it finished.',
};
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

/*
 * Wait-in-flow progress view (UI design §2.2/§2.4). On mount, always makes ONE `get` call
 * for the job before showing anything else -- rehydrating a lost in-memory jobId via
 * `getForModel` first (a browser refresh), then polling `get` on a timer until a terminal
 * status. On PROPOSAL_READY it navigates into the real model (Phase 1's review surface
 * takes it from there). On any of the three terminal-error statuses it stops polling and
 * shows the job's `reason` + "try another document" instead of progress -- whether that
 * terminal status was reached by a live poll or discovered fresh on a reload.
 */
const ExtractionProgress = ({
  model,
  routeParams,
  location,
  navigate,
  fetchModel,
  getExtractionJob,
  rehydrateExtractionJob,
  chooseUploadReport,
}: Props): Node => {
  const { modelId } = routeParams;
  const [jobId, setJobId] = useState(location.state?.jobId || null);
  const [jobState, setJobState] = useState(null);
  const [noJob, setNoJob] = useState(false);
  const [pollFailed, setPollFailed] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    fetchModel(modelId);
  }, [modelId, fetchModel]);

  // What to do with a fetched `getResponse` payload -- shared by the first mandatory call,
  // every subsequent poll tick, and a dev-jump -- so all three paths treat a terminal
  // status (PROPOSAL_READY or one of the three terminal errors, AU-9) identically.
  const applyJobResponse = payload => {
    setJobState(payload);
    const { enterModel, stopPolling } = resolveExtractionJobAction(payload);
    if (enterModel) {
      navigate(`/${MODELS}/${modelId}`, { replace: true });
    }
    return stopPolling;
  };

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;
    let consecutiveFailures = 0;
    setPollFailed(false);

    const pollOnce = currentJobId =>
      getExtractionJob(modelId, currentJobId).then(({ payload }) => payload);

    const start = async () => {
      try {
        // Always rehydrates (if needed) and makes exactly one `get` call before showing
        // anything else (UI design §2.2) -- this is what makes a reload mid-wait, or a
        // reload onto an already-terminal job (AU-9), behave identically to a live poll.
        const resolvedJobId = await rehydrateExtractionJob(modelId, jobId);
        if (cancelled) {
          return;
        }
        if (!resolvedJobId) {
          setNoJob(true);
          return;
        }
        setJobId(resolvedJobId);

        const payload = await pollOnce(resolvedJobId);
        if (cancelled) {
          return;
        }
        const terminal = applyJobResponse(payload);
        if (terminal) {
          return;
        }

        intervalId = setInterval(async () => {
          try {
            const nextPayload = await pollOnce(resolvedJobId);
            if (cancelled) {
              return;
            }
            consecutiveFailures = 0;
            const nextTerminal = applyJobResponse(nextPayload);
            if (nextTerminal && intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          } catch {
            if (!cancelled) {
              consecutiveFailures += 1;
              if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
                clearInterval(intervalId);
                intervalId = null;
                setPollFailed(true);
              }
            }
          }
        }, POLL_INTERVAL_MS);
      } catch {
        // A transient HTTP/network failure (a dropped connection, a 500, an expired
        // session) on the mandatory rehydrate/first-poll call -- surface a retryable
        // error rather than leaving the screen stuck on "Loading extraction status…"
        // forever with an unhandled rejection.
        if (!cancelled) {
          setPollFailed(true);
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // Re-runs when the model changes, or when `retryToken` is bumped by the user clicking
    // "Try again" after a failed poll -- restarting from the mandatory rehydrate/first-poll
    // call, exactly like a fresh mount would. `jobId` is read once at effect start (via the
    // `jobId` state seeded from location.state) and thereafter owned by this effect's own
    // polling loop, not by React's dependency-driven re-run.
  }, [modelId, retryToken]);

  const handleRetryPoll = () => {
    setRetryToken(token => token + 1);
  };

  const handleTryAnotherDocument = () => {
    // A legitimate in-UI route back to the upload step (HBR-4950 #316) -- without this, the
    // upload route's RequireCreateModelFlowStep guard (step="upload") would bounce it straight
    // back to the model, since this screen's own ephemeral step is never 'upload'.
    chooseUploadReport(modelId);
    navigate(`/${MODELS}/${modelId}/${UPLOAD}`);
  };

  if (noJob) {
    return <Navigate to={`/${MODELS}/${modelId}/${UPLOAD}`} replace />;
  }

  const { isTerminalError: isError, enterModel: isProposalReady } = jobState
    ? resolveExtractionJobAction(jobState)
    : {};

  return (
    <Page className="model-type-page extraction-progress-page">
      <Header />
      <Main className={layout.centeredMain}>
        <Row className="justify-content-center">
          <Col md="10" lg="8" xl="7" className="col text-center">
            <h1 className={layout.title}>
              SP3-AI-<em>ReportReader</em>
            </h1>
            {!pollFailed && isError ? (
              <p className={layout.intro}>
                SP3-AI couldn&apos;t finish reading the report
                {model?.modelName && (
                  <>
                    {' '}
                    for <strong>{model.modelName}</strong>
                  </>
                )}
                .
              </p>
            ) : (
              <p className={layout.intro}>
                {model?.modelName ? (
                  <>
                    Reading the structural report for <strong>{model.modelName}</strong>.
                  </>
                ) : (
                  'Reading your structural report.'
                )}{' '}
                When it&apos;s done, you&apos;ll review the proposed inputs in the model.
              </p>
            )}
          </Col>
        </Row>
        <Row className="justify-content-center">
          <Col md="10" lg="8" xl="7" className={`col ${styles.status}`}>
            {pollFailed && (
              <>
                <Alert color="danger">
                  We couldn&apos;t check on the report&apos;s progress. It&apos;s still being read;
                  this is only a connection problem.
                </Alert>
                <Button color="primary" onClick={handleRetryPoll}>
                  Try again
                </Button>
              </>
            )}
            {!pollFailed && jobState && isError && (
              <>
                <Alert color="danger">
                  {jobState.reason ||
                    NO_REASON_FALLBACK[jobState.status] ||
                    NO_REASON_FALLBACK.DEFAULT}
                </Alert>
                <TryAnotherDocumentButton onClick={handleTryAnotherDocument} />
              </>
            )}
            {!isError && !isProposalReady && jobState && (
              <ExtractionStages
                status={jobState.status}
                progress={jobState.progress}
                live={!pollFailed}
              />
            )}
            {isProposalReady && (
              <p className={styles.stage}>Done. Taking you to the model to review…</p>
            )}
            {!jobState && !isError && !pollFailed && (
              <p className={styles.stage}>Checking progress…</p>
            )}
          </Col>
        </Row>
      </Main>
    </Page>
  );
};

export default ExtractionProgress;
